import { CaregiverIdentity } from 'hooks/useCaregivers';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'store/users/selectors';
import CaregiverAvatar from './CaregiverAvatar';

function Title({ referent }: { referent: CaregiverIdentity }) {
  return (
    <Trans i18nKey="messagingV3.hero.title" values={{ firstName: referent.firstName }} components={{ br: <br /> }} />
  );
}

/**
 * « Laurine vous répond » on the homepage card, before any conversation: her
 * promise beside her portrait, and a first word from her (Figma « Carte
 * référente »).
 */
export function ReferentCard({ referent }: { referent: CaregiverIdentity }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4.5" data-testid="referent-hero">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 className="text-2xl tracking-normal">
            <Title referent={referent} />
          </h2>
          <p className="text-sm text-secondary-foreground">{t('messagingV3.hero.subtitle')}</p>
        </div>
        <CaregiverAvatar caregiver={referent} size="portrait" className="border-2" tinted />
      </div>
      <p className="rounded-lg rounded-tr-sm bg-chat-them px-3.5 py-2.5 text-sm text-chat-them-foreground shadow-xs">
        {t('messagingV3.hero.bubble')}
      </p>
    </div>
  );
}

/**
 * « Laurine vous répond » at the top of the messaging page, before any
 * conversation: her portrait, name and job, and a first word to the patient
 * (Figma « Hero »).
 */
export default function ReferentHero({ referent }: { referent: CaregiverIdentity }) {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const patientFirstName = currentUser?.first_name;

  return (
    <div className="flex flex-col gap-5" data-testid="referent-hero">
      <h1>
        <Title referent={referent} />
      </h1>
      <div className="flex items-center gap-4">
        <CaregiverAvatar caregiver={referent} size="xl" className="border-2" tinted />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-base font-medium text-foreground">{referent.name}</p>
          <p className="text-sm text-muted-foreground">
            {referent.role && (
              <>
                {t('messagingV3.referentRole', { role: referent.role })}
                <br />
              </>
            )}
            {t('messagingV3.hero.delay')}
          </p>
        </div>
      </div>
      <p className="rounded-lg rounded-tl-sm bg-chat-them px-3.5 py-2.5 text-base text-chat-them-foreground">
        {patientFirstName
          ? t('messagingV3.hero.bubbleTo', { firstName: patientFirstName })
          : t('messagingV3.hero.bubble')}
      </p>
    </div>
  );
}
